import axios, { AxiosInstance, AxiosResponse } from 'axios';
import EventEmitter from 'eventemitter3';
import jwtDecode from 'jwt-decode';
import { container, singleton } from 'tsyringe';
import { v4 as uuidv4 } from 'uuid';
import Logger from '../../gamma-engine/core/utils/Logger';
import { clusterData, RoundResult, SpinResult } from '../../gamma-engine/slots/model/RoundResult';
import SlotMachine from '../../gamma-engine/slots/model/SlotMachine';
import {
    PatternType,
    SlotMachineDescription,
    SlotMachineType
} from '../../gamma-engine/slots/model/SlotMachineDescription';
import Wallet from '../../gamma-engine/slots/model/Wallet';
import { getFromLocalStorage, saveToLocalStorage } from '../model/LocalStorageUtils';
import { SettingsType } from '../model/SettingsType';
import { TransactionType } from '../model/TransactionType';
import { LoginResponse, TransactResponse } from './Responses';
import { GameServiceEvent } from './event/GameServiceEvent';
import { currencyCode } from './text-json-files/DataSource';
import ICommonGameService from '../../gamma-engine/common/services/ICommonGameService';
import History from '../../gamma-engine/common/model/History';
import { HistoryReplayDetails } from '../../gamma-engine/common/model/HistoryReplayDetails';
import { ERROR_CONSTANTS, FORCING_DATA, GAME_NAME, LOGIN_CURRENT, REEL_DATA, RULES } from './GameConstant';

@singleton()
export default class GameService extends EventEmitter implements ICommonGameService{

    // token will refresh if less than X seconds left to expiry
    private tokenRefreshThreshold: number = 300;

    private gameCode: string;
    private jwtToken: string;
    private ax: AxiosInstance;
    private hx:AxiosInstance;
    private lobbyUrl: string;

    private featureBuy: {
        RoundType: number,
        Rate: number,
        Total: number
    }

    private _doubleChanceEnabled: boolean = false;
    private _settings: SettingsType;
    private sm: SlotMachine;

    constructor(
        baseUrl: string,
        jwtToken: string,
        gameCode: string,
        lobbyUrl: string = '',
        private isReplay?: boolean,
        private replayData?: any,
        public isForcing?: boolean,
        public playerProtectionConfig: {
            enabled: boolean;
            startFrom?: { hours: number, minutes: number, seconds: number };
            timeLimit?: { hours: number, minutes: number, seconds: number };
            repeatInterval?: { hours: number, minutes: number, seconds: number };
        } = { enabled: false }
    ) {
        super();
        this.jwtToken = jwtToken;
        this.gameCode = gameCode;
        this.lobbyUrl = lobbyUrl;
        this.ax = axios.create({
            baseURL: baseUrl,
            headers: {
                'Authorization': `Bearer ${this.jwtToken}`
            }
        });
        this.hx = axios.create({
            baseURL: baseUrl.replace('/engine-six',''),
            headers: {
                'Authorization': `Bearer ${this.jwtToken}`
            }
        });

        this.ax.interceptors.request.use(async config => {
            if (config.url != 'keep-alive' && !this.isReplay) {
                await this.checkAndUpdateToken();
            }
            return config;
        }, error => {
            Promise.reject(error)
        });

        this._settings = getFromLocalStorage('settings') ??{
            quickSpin: false,
            batterySaver: false,
            ambientMusic: true,
            soundFx: true,
            introScreen: true,
            skipBigWin: false,
        };
    }

    // PUBLIC REPLAY API
    public async replayInitialize(): Promise<[Wallet, SlotMachine]> {

        // const response = await fetch("src/jewels-of-wizard/services/transact.json");
        // this.replayData = await response.json();

        let data: LoginResponse = {
            Player: {
                Balance: 0,
                Rate: 1,
                Currency: this.replayData?.bets ? this.replayData?.bets[0]?.player.Currency : "EUR",
                CoinRate: 100,
            },
            Current: LOGIN_CURRENT,
            Next: { Type: 1 },
            Details: {
                betId: "85422",
                gameId: 7,
                userId: "17",
            },
            GameInfo: {
                Line: 20,
                BetValue: [1],
                 defaultBetValue:1,
                LineBet: [
                    0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.7, 1, 1.05,
                    1.4, 1.5, 1.75, 2, 2.1, 2.45, 2.5, 2.8, 3, 3.15, 3.5, 4, 4.5, 5, 6,
                    7, 7.5, 8, 9, 10, 12, 12.5, 15, 17.5, 18, 20, 22.5, 24, 25, 30, 36, 42,
                    48, 54, 60,
                ],
                BuyFeature:
                {
                    RoundType: 0,
                    Rate: 100,
                    Total: 15,
                },
            }
        };
        const denomination: number = 1;
        const wallet: Wallet = new Wallet(denomination, {
            isoCode: currencyCode[this.replayData?.bets ? this.replayData?.bets[0]?.player.Currency : "EUR"],
            precision: 2
        }, [1]);

        const description: SlotMachineDescription = {
            rtp: 'unknown',
            coinRate: 100,
            defaultValue: data.GameInfo.LineBet[data.GameInfo.defaultBetValue] ?? data.GameInfo.LineBet[0],
            type: SlotMachineType.COMBINATIONS,
            betLimits: data.GameInfo.LineBet,
            betMaxQuantity: 10,
            totalWinMultipliers: [
                1, 2, 3, 4, 5
            ],
            combinations: 20,
            bigWinMultiplierLevels: [10, 20, 30, 40, 5000],
            symbols: [25, 104, 103, 102, 101, 203, 202, 201],
            reels: REEL_DATA,
            rules: RULES
        };

        const slotMachine: SlotMachine = new SlotMachine(description, {
            gameSpeedLevels: 2
        });

        this.sm = slotMachine;
        slotMachine.isReplay = this.isReplay;
        slotMachine.forcingArray = FORCING_DATA;

        const isInvalidReplay = this.replayData === ERROR_CONSTANTS.ROUND_NOT_FINISHED || this.replayData === ERROR_CONSTANTS.INVALID_ROUND_ID || this.replayData?.bets?.[0]?.game?.name !== GAME_NAME;
        const isValidGame = this.replayData?.bets?.[0]?.game?.name === GAME_NAME;

        if(isInvalidReplay) {
            slotMachine.betStates = [];
            wallet.balance = 0;

            slotMachine.replayData.initialBalance = 0;
            slotMachine.replayData.betAmount = 0;
            slotMachine.currentBetValue = 0;

            if(this.replayData === ERROR_CONSTANTS.ROUND_NOT_FINISHED) slotMachine.replayData.replayError = ERROR_CONSTANTS.ROUND_NOT_FINISHED;
            else if (this.replayData === ERROR_CONSTANTS.INVALID_ROUND_ID) slotMachine.replayData.replayError = ERROR_CONSTANTS.INVALID_ROUND_ID;
            else slotMachine.replayData.replayError = isValidGame ? ERROR_CONSTANTS.INVALID_ROUND_ID : ERROR_CONSTANTS.INVALID_GAME;
        } else {
            slotMachine.betStates = this.replayData?.bets[0]?.betStates;
            wallet.balance = this.replayData?.bets[0]?.player.Balance + this.replayData?.totalBetAmount - this.replayData?.totalWinAmount;
            slotMachine.replayData.initialBalance = this.replayData?.bets[0]?.player.Balance + this.replayData?.totalBetAmount - this.replayData?.totalWinAmount;
            slotMachine.replayData.betAmount = this.replayData?.bets[0]?.betAmount;

            slotMachine.currentBetValue =
                slotMachine.betStates && slotMachine.betStates.length > 0
                    ? slotMachine.betStates[0]?.Current?.Round?.ActualBet /
                    (this.doubleUpChance ? 25 : 20)
                    : description.betLimits[0] / 20;
        }

        slotMachine.currentGameSpeedLevel = !this._settings.quickSpin ? 0 : 1;
        let roundResult: RoundResult;
        roundResult = this.parseTransactDataToRoundResult(data);
        slotMachine.roundResult = roundResult;
        // slotMachine.initialLineBet = roundResult.lineBetValue;
        slotMachine.previousRoundResult = slotMachine.getDummyRoundResult(roundResult);

        this.featureBuy = {
            RoundType: 2,
            Total: 15,
            Rate: 100
        };

        return [wallet, slotMachine];
    }

    // PUBLIC API
    public async initialize(): Promise<[Wallet, SlotMachine]> {
        if (this.isReplay) {
            const [wallet, slotMachine] = await this.replayInitialize();
            return [wallet, slotMachine]
        }
        let data: LoginResponse;
        try {
            data = await this.login();
        } catch(e) {
            Logger.error(e);
            this.emit(GameServiceEvent.ERROR);
            throw(e);
        }

        const denomination: number = 1;
        const wallet: Wallet = new Wallet(denomination, {
            isoCode: currencyCode[data.Player.Currency],
            precision: 2
        }, [1]);

        const description: SlotMachineDescription = {
            rtp: 'unknown',
            coinRate:data.Player.CoinRate,
            defaultValue: data.GameInfo.LineBet[data.GameInfo.defaultBetValue] ?? data.GameInfo.LineBet[0],
            type: SlotMachineType.COMBINATIONS,
            betLimits: data.GameInfo.LineBet,
            betMaxQuantity: 10,
            totalWinMultipliers: [
                1, 2, 3, 4, 5
            ],
            combinations:20,
            bigWinMultiplierLevels: [10, 20, 30, 40, 5000],
            symbols: [25, 104, 103, 102, 101, 203, 202, 201],
            reels: REEL_DATA,
            rules: RULES
        };

        wallet.balance = data.Player.Balance;

        const slotMachine: SlotMachine = new SlotMachine(description, {
            gameSpeedLevels: 2
        });

        this.sm = slotMachine;
        this.sm.forcingArray = FORCING_DATA;

        /* eslint-disable */
        let roundResult: RoundResult;
        if(data.Current?.Round){
            slotMachine.currentBetValue =  this.getLineBetAmount(data.Current.Round.LineBet, data.GameInfo.LineBet) * Wallet.denomination;
        }
        else{
            data.Current = LOGIN_CURRENT;
        }

        roundResult = this.parseTransactDataToRoundResult(data);
        slotMachine.roundResult = roundResult;
        slotMachine.initialLineBet = roundResult.lineBetValue;
        slotMachine.previousRoundResult = slotMachine.getDummyRoundResult(roundResult);
        if(data.Current?.Bonus ) {
             slotMachine.bonusGameClaimdValue = data.Current.Bonus?.totalWin;
        }
        if(data.Next.Bonus) {
            slotMachine.currentSpinResult.bonus = {
                bonusGameid: data.Next.Bonus.id,
                lineBet: data.Next.Bonus.lineBet,
                expiredInMS: Math.max(new Date(data.Next.Bonus.expiredAt).getTime() - new Date().getTime(), 0),
                totalCount: data.Next.Bonus.spinCount,
                remainingCount: data.Next.Bonus.spinLeft,
                winAmount: data.Next.Bonus.totalWin,
                roundComplete: false
            }
           
        }

        this.featureBuy = {
            RoundType: data.GameInfo.BuyFeature ? data.GameInfo.BuyFeature[0].roundType: 1,
            Total: data.GameInfo.BuyFeature ? data.GameInfo.BuyFeature[0].Total: 15,
            Rate: data.GameInfo.BuyFeature ? data.GameInfo.BuyFeature[0].Rate: 100
        };
        slotMachine.currentGameSpeedLevel = !this._settings.quickSpin ? 0 : 1;
        return [wallet, slotMachine];
    }

    async transactHistoryDetail(spinsCount: number): Promise<HistoryReplayDetails> {
        const sm = container.resolve(SlotMachine);
        let count = sm.historyReplay.spinsCount;
        count++;
        sm.historyReplay.spinsCount = count;
        return sm.betStates[sm.historyReplay.spinsCount];
    }

    public async getHistoryResponse(page: number,perPage:number): Promise<any> {
        const historyResponse = await this.getMyBets(page,perPage);
        const history: History = container.resolve(History);
        history.currentPage = historyResponse.page;
        history.totalPages = historyResponse.totalPages
        history.entries=[];
        historyResponse.bets.forEach((historyData) => {
            history.entries.push({
                datetime: historyData.createdAt,
                balance: historyData.player.Balance,
                win: Number(historyData.winningAmount) + Number(historyData.freeSpinWinningAmount),
                totalBet: historyData.betAmount,
                betId: historyData.id
            });
        })
        return historyResponse;

    }
    public async getMyBets(page?: number,perPage?:number): Promise<any> {
         const res: AxiosResponse = await this.hx.get('my-bets', {
             params: {
                 page: page ? page : 1,
                 perPage:perPage
             }
         });
         return res.data.data;
     }

    getLineBetAmount(lineBet: number, linebetArray: number[]){
        if(linebetArray.includes(lineBet))
            return lineBet;
        else
            return linebetArray[0]
    }

    public get doubleUpChance(): boolean{
        return this._doubleChanceEnabled;
    }

    public set doubleUpChance(isActive: boolean){
        if(this._doubleChanceEnabled == isActive)
            return;

        this._doubleChanceEnabled = isActive;
        this.emit(GameServiceEvent.DOUBLE_CHANCE_CHANGED);
    }

    public async spin(): Promise<RoundResult> {
        const sm: SlotMachine = container.resolve(SlotMachine);
        const wallet: Wallet = container.resolve(Wallet);

        const totalBet: number = (sm.totalBet  * wallet.coinValue);
        const singleBet: number = totalBet/sm.combinations;
        return this.getFullRoundResultFromTransact(1, singleBet);
    }

    public async buyFeature(): Promise<RoundResult> {
        const sm: SlotMachine = container.resolve(SlotMachine);
        const wallet: Wallet = container.resolve(Wallet);

        const totalBet: number = (sm.totalBet  * wallet.coinValue);
        const singleBet: number = totalBet/sm.combinations;
        const lastRound: SpinResult = sm.previousRoundResult.spins[sm.previousRoundResult.spins.length - 1];
        lastRound.bonus = null; // to avoid passing old bonus id
        return this.getFullRoundResultFromTransact(2, singleBet);
    }

    public async login(): Promise<LoginResponse> {
        Logger.debug('GameService.login');

        const res: AxiosResponse = await this.ax.post('preload', {
            GameCode: this.gameCode
        });

        return res.data.data;
    }

    public async keepAlive(): Promise<void> {
        Logger.debug('GameService.keepAlive');

        const res: AxiosResponse = await this.ax.post('keep-alive', {
            GameCode: this.gameCode
        })
        this.jwtToken = res.data.data.Jwt;
        this.ax.defaults.headers.Authorisation = `Bearer ${this.jwtToken}`;
    }

    //Round type: 1 - base game, 2 - feature buy
    public async transact(lineBet: number, numLines: number, type: number, roundType: number = 1, bonusId: string = null): Promise<TransactResponse> {
        const res: AxiosResponse = await this.ax.post('transact', {
            bonusId: bonusId,
            Type: type ?? 1,
            BetValue: 1,
            Line:numLines,
            LineBet: lineBet,
            RoundType: this.doubleUpChance?3:roundType,
            forcing: this.sm.forcing,
            force: this.sm.selectedOption
        });
        Logger.debug(res.data.data)
        return res.data.data;
    }

    public lobby():void{
        Logger.debug('GameService.lobby');
        if(typeof this.lobbyUrl!='undefined' && this.lobbyUrl)
            window.open(this.lobbyUrl, '_self');
        else
            history.back();
    }

    public get featureBuyConfig(){
        return this.featureBuy;
    }

    public saveSettings(){
        saveToLocalStorage('settings', this._settings);
        this.emit(GameServiceEvent.SETTINGS_CHANGED, this);
    }

    public get settings(){
        return this._settings;
    }

    // PRIVATE API
    private async checkAndUpdateToken(): Promise<void> {
        const expirationTimestamp: number = jwtDecode(this.jwtToken)['exp'] * 1000;
        const timeLeftSeconds: number = (expirationTimestamp - Date.now()) / 1000;

        if (timeLeftSeconds < this.tokenRefreshThreshold) {
            await this.keepAlive();
        }
    }

    protected async getFullRoundResultFromTransact(roundType: number = 1, bet: number): Promise<RoundResult>{
        const sm: SlotMachine = container.resolve(SlotMachine);
        const wallet: Wallet = container.resolve(Wallet);
        let data: TransactResponse = null;
        const lastRound: SpinResult = sm.previousRoundResult.spins[sm.previousRoundResult.spins.length - 1];

        const totalBet: number = (sm.totalBet  * wallet.coinValue);
        const singleBet: number = totalBet/sm.combinations;

        try {
            if ((sm.historyReplay.enabled && !sm.historyReplay._skipreplay) || sm.isReplay) {
                data = await this.transactHistoryDetail(sm.historyReplay.spinsCount);
            } else {
                data = await this.transact(bet, sm.combinations,sm.previousRoundResult.nextType, roundType, lastRound.bonus?.bonusGameid ?? null);
            }
            sm.isTransact = true;
        } catch(e) {
            Logger.error(e);
            this.emit(GameServiceEvent.ERROR);
            throw(e);
        }

        let roundResult: RoundResult = this.parseTransactDataToRoundResult(data);
        const previousSpins: SpinResult[] = roundResult.spins;

        //get cascade spins till its finished
        while(roundResult.nextType && (roundResult.nextType === TransactionType.RESPIN || roundResult.nextType === TransactionType.RESPIN_IN_FREESPIN)){
            try {
                if ((sm.historyReplay.enabled && !sm.historyReplay._skipreplay )|| sm.isReplay) {
                    data = await this.transactHistoryDetail(sm.historyReplay.spinsCount);
                } else {
                    data = await this.transact(singleBet/ Wallet.denomination, sm.combinations, roundResult.nextType, 1, lastRound.bonus?.bonusGameid ?? null);
                }
                sm.isTransact = true;
            }
            catch(e){
                Logger.error(e);
                this.emit(GameServiceEvent.ERROR);
                throw(e);
            }

            const newResult: RoundResult = this.parseTransactDataToRoundResult(data);

            newResult.spins.forEach((spinResult)=>{
                previousSpins.push(spinResult);
            });

            roundResult = newResult;
            roundResult.spins = previousSpins;
        }

        return roundResult;
    }

    private parseTransactDataToRoundResult(data:TransactResponse | LoginResponse, fullParse: boolean = true):RoundResult{
        const reelsOutput: number[][] = this.parseReelsOutput(data.Current.Result.R)
        const roundResult: RoundResult = {
            complete: false,
            id: uuidv4(),
            lineBetValue: data.Current.Round.LineBet * Wallet.denomination,
            betLines: data.Current.Round.Line,
            totalBet: data.Current.Round.Bet,
            totalWinValue: data.Current.Round.Payout,
            roundType: data.Current.Round.RoundType,
            type:data.Current.Type,
            spinIndex: 0,
            details:data.Details ? {
                betId:data.Details.betId,
                gameId:data.Details.gameId,
                userId:data.Details.userId
            }: null,
            nextType: data.Next.Type,
            currentType : data.Current,
            spins: [
                {
                    result: reelsOutput,
                    multipliers: this.parseMultipliersOutput(data.Current.MultiplierMap, reelsOutput.length),
                    winValue: data.Current.TotalWin,
                    currentTotalWinValue: data.Current.AccWin,
                    maxWin: data.Next?.isMaxWin ?? false,
                    win: ((data.Current.Result.clusterDetails && data.Current.Result.clusterDetails.length > 0) || data.Current.Result.scatter) ? {
                        multiWinShown: data.Current.Result?.scatter ? true : false,
                        scatterWinShown: data.Current.Result?.scatter ? false : true,
                        winningPattern: data.Current.Result?.scatter ? this.parseScatterWins(data.Current.Result.scatter, reelsOutput.length) : this.parseWaysWins(data.Current.Result.clusterDetails, reelsOutput.length)
                    }:null,
                    freespins: (data.Next.FreeSpin || data.Current.FreeSpin) ? {
                        freespinId: data.Current.FreeSpin ? data.Current.FreeSpin.Current : 0,
                        moreAwarded: data.Next.FreeSpin?.MoreAwarded ? data.Next.FreeSpin.MoreAwarded : null,
                        IsmoreAwarded: data.Next.FreeSpin?.MoreAwarded ? true : false,
                        totalCount: data.Next.FreeSpin ? data.Next.FreeSpin.Total : data.Current.FreeSpin.Total,
                        remainingCount: data.Next.FreeSpin? data.Next.FreeSpin.Total - (data.Next.FreeSpin.Next-1):data.Current.FreeSpin? data.Current.FreeSpin.Total - data.Current.FreeSpin.Current: 0,// (data.Current.FreeSpin ? data.Current.FreeSpin.Total - (data.Current.FreeSpin.Current+1) :(data.Next.FreeSpin?data.Next.FreeSpin.Total:0)),
                        roundStarted: (data.Current.FreeSpin && data.Current.FreeSpin.Current>0),
                        roundComplete: false,
                    }:null,
                    bonus: (data.Current.Bonus || data.Next.Bonus) ? {
                        bonusGameid: data.Current.Bonus?.id ?? data.Next.Bonus.id,
                        lineBet: data.Current.Bonus?.lineBet ?? data.Next.Bonus.lineBet,
                        expiredInMS: Math.max(new Date(data.Current.Bonus?.expiredAt ?? data.Next.Bonus.expiredAt).getTime() - new Date().getTime(), 0),
                        totalCount: data.Current.Bonus?.spinCount ?? data.Next.Bonus.spinCount,
                        remainingCount: data.Current.Bonus?.spinLeft ?? data.Next.Bonus.spinLeft,
                        winAmount: data.Current.Bonus?.totalWin ?? data.Next.Bonus.totalWin,
                        roundComplete: false
                    } : null,
                    nextBonus: (data.Next.Bonus && data.Current.Bonus?.spinLeft === 0) ? {
                        bonusGameid: data.Next.Bonus.id,
                        lineBet: data.Next.Bonus.lineBet,
                        expiredInMS: Math.max(new Date(data.Next.Bonus.expiredAt).getTime() - new Date().getTime(), 0),
                        totalCount: data.Next.Bonus.spinCount,
                        remainingCount: data.Next.Bonus.spinLeft,
                        winAmount: data.Next.Bonus.totalWin,
                        roundComplete: false
                    } : null
                }
            ]
        };
        return roundResult;
    }

    private parseWaysWins(clusterDetails: {
        symbol: number | string,
        positions: string[],
        payout: number,
        multiplier?: number | string,
        multiplierPositions?: string[],
    }[], reelsLength: number): Map<number, clusterData[]>{

        const winningSymbols: Map<number, clusterData[]> = new Map<number, clusterData[]>();

        clusterDetails.forEach((cluster) => {
            let symbolCount: number = 0;
            const pattern: number[][] = Array.from({ length: reelsLength }, () => Array(reelsLength).fill(0));
            let payout: number = 0;
            let multiplier: number = 0;
            const multipliersPosition: number[][] = Array.from({ length: reelsLength }, () => Array(reelsLength).fill(0));

            cluster.positions.forEach((pos) => {
                const [row, col] = pos.split(',').map(Number);
                pattern[row][col] = 1;
                symbolCount++;
            })
            payout = cluster.payout;
            multiplier = Number(cluster.multiplier);

            // If the symbol already exists, push to the existing array
            const symbolKey = Number(cluster.symbol);
            if (winningSymbols.has(symbolKey)) {
                winningSymbols.get(symbolKey)?.push({
                    symbolCount: symbolCount,
                    pattern: pattern,
                    payout: payout,
                    multiplier: multiplier,
                    multipliersPosition: multipliersPosition
                });
            } else {
                winningSymbols.set(symbolKey, [{
                    symbolCount: symbolCount,
                    pattern: pattern,
                    payout: payout,
                    multiplier: multiplier,
                    multipliersPosition: multipliersPosition
                }]);
            }
        })

        return winningSymbols;
    }

    private parseReelsOutput(data: string): number[][] {
        return data.split('|')
            .map((value: string): number[] => {
                return value.split(',')
                    .map((value: string): number => {
                        return parseInt(value);
                    })
            });
    }

    private parseMultipliersOutput(data: { [key: string]: number }, reelsLength: number): number[][] {
        const multipliers: number[][] = Array.from({ length: reelsLength }, () => Array(reelsLength).fill(0));

        if (data) {
            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    const [row, col] = key.split(',').map(Number);
                    multipliers[row][col] = Number(data[key]);
                }
            }
        }

        return multipliers;
    }

    private parseScatterWins(scatterDetails: {
        symbol: number,
        payout: number,
        positions: string[]
    }, reelsLength: number): Map<number, clusterData[]>{

        const winningSymbols: Map<number, clusterData[]> = new Map<number, clusterData[]>();
        const scatterPattern: number[][] = Array.from({ length: reelsLength }, () => Array(reelsLength).fill(0));
        let symbolCount: number = 0;

        scatterDetails.positions.forEach((pos) => {
            const [row, col] = pos.split(',').map(Number);
            scatterPattern[row][col] = 1;
            symbolCount++;
        })

        winningSymbols.set(scatterDetails.symbol, [{
            symbolCount: symbolCount,
            pattern: scatterPattern,
            payout: scatterDetails.payout
        }])

        return winningSymbols;
    }
}
